/**
 * Groups used to organize theme CSS variables. The two "editable" groups
 * (primary/accent) are surfaced in the org rebrand editor; the rest exist only
 * to categorize the runtime palette. `background` used to be a third — it holds
 * no editable var any more, since the whole surface ladder is toned from the
 * brand hue (D-8) and a tenant writes those through its own path — and `text`
 * and `action` were the fourth and fifth until rebrand v2 made text colour
 * automatic: their vars stay in the catalog, derived, but nobody picks them.
 */
export type ThemeVarGroup =
  | 'accent'
  | 'action'
  | 'background'
  | 'common'
  | 'error'
  | 'event'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'text'
  | 'warning';

/**
 * How a derived var's value is computed from its `from` source var. The source
 * color may be a hex (`#rgb` / `#rrggbb`) OR an `rgb()`/`rgba()` string.
 *
 * - `rgbChannel` — source -> `'r g b'`, for `rgba(var(--…-mainChannel) / a)`
 * - `alpha`      — source -> `rgba(r, g, b, <alpha>)`
 * - `contrast`   — source -> the higher-contrast of `#000000` / `#FFFFFF`
 * - `shift`      — source in OKLCH -> `L + dL`, `C * fC`, hue kept. Builds the
 *                  brand ramps, so a rebranded tenant's lighter/darker steps
 *                  follow its own color instead of the previous brand's.
 * - `tone`       — absolute `L` and `C` at the SOURCE'S hue. Builds the surface
 *                  ladder, which is why the whole chrome carries the brand hue.
 *
 * `shift` and `tone` gamut-map their result, so an out-of-sRGB request lands on
 * the nearest displayable color at the same lightness and hue.
 */
export type ThemeVarTransform =
  | { kind: 'rgbChannel' }
  | { kind: 'contrast' }
  | { kind: 'alpha'; alpha: number }
  | { kind: 'shift'; dL: number; fC: number }
  | { kind: 'tone'; L: number; C: number };

export interface ThemeVarDerived {
  /** varName of the catalog entry this value is computed from */
  from: string;
  transform: ThemeVarTransform;
}

export interface ThemeVarDefinition {
  /** CSS custom property name, e.g. '--mfw-palette-primary-main' */
  varName: string;
  /**
   * @mfw/translations message id. Only set for the 2 editable vars (the brand
   * anchors), since those are the only ones ever surfaced in an editor UI.
   */
  labelKey?: string;
  /**
   * @mfw/translations message id for the info-tooltip description shown next
   * to the picker in the rebrand editor. Convention: `labelKey + 'Description'`.
   * Only set for the 2 editable vars.
   */
  descriptionKey?: string;
  group: ThemeVarGroup;
  /** Verbatim runtime value from AppThemeProvider's darkTheme */
  defaultValue: string;
  /** Editable in the future org rebrand editor */
  editable: boolean;
  /** Present for vars that are auto-computed from another catalog entry */
  derived?: ThemeVarDerived;
}

/**
 * Translation ids for the 2 editable group names, used by the org rebrand
 * editor to label each section.
 */
export const THEME_VAR_GROUP_LABEL_KEYS: Record<'accent' | 'primary', string> =
  {
    accent: 'themeVars.groupAccent',
    primary: 'themeVars.groupPrimary',
  };

/**
 * Single source of truth for every `--mfw-palette-*` CSS variable the app
 * applies to `:root`. `defaultValue` is verbatim from the runtime `darkTheme`
 * object in AppThemeProvider (packages/@mfw-frontend/src/context/AppThemeProvider.tsx) —
 * including non-hex values (rgba/channel/var()-referencing strings) — since
 * that's what users see today and `resolveThemeDefaults()` must reproduce it
 * byte-for-byte.
 */
export const THEME_VARS: readonly ThemeVarDefinition[] = [
  {
    varName: '--mfw-palette-accent-contrastText',
    group: 'accent',
    defaultValue: '#000000',
    editable: false,
    derived: {
      from: '--mfw-palette-accent-main',
      transform: { kind: 'contrast' },
    },
  },
  {
    varName: '--mfw-palette-accent-dark',
    group: 'accent',
    defaultValue: '#0080bc',
    editable: false,
    derived: {
      from: '--mfw-palette-accent-main',
      transform: { kind: 'shift', dL: -0.13, fC: 0.92 },
    },
  },
  {
    varName: '--mfw-palette-accent-light',
    group: 'accent',
    defaultValue: '#5fbffd',
    editable: false,
    derived: {
      from: '--mfw-palette-accent-main',
      transform: { kind: 'shift', dL: 0.07, fC: 0.85 },
    },
  },
  {
    varName: '--mfw-palette-accent-main',
    labelKey: 'themeVars.accentMain',
    descriptionKey: 'themeVars.accentMainDescription',
    // The second brand anchor. A gradient needs two, and deriving the second by
    // a fixed hue rotation was measured to walk warm brands into unrelated hues
    // (amber +79° lands on green) — so it is an input, not a derivation.
    // Default is the azure sampled from the client's mockup CTA.
    group: 'accent',
    defaultValue: '#27a9f1',
    editable: true,
  },
  {
    varName: '--mfw-palette-accent-mainChannel',
    group: 'accent',
    defaultValue: '39 169 241',
    editable: false,
    derived: {
      from: '--mfw-palette-accent-main',
      transform: { kind: 'rgbChannel' },
    },
  },
  /**
   * Icon and control ink. Same story as the text ladder: it was a free-standing
   * grey chosen against the dark surfaces, and it drops to 2.26:1 on a cream
   * ladder — under even the 3:1 that SC 1.4.11 asks of a control. As an alpha
   * step of `text-primary` it follows the surface instead, and stays close to
   * the old swatch on the dark ladder it was picked for. Not editable (rebrand
   * v2): no layer can set it, see `text-primary`.
   */
  {
    varName: '--mfw-palette-action-active',
    group: 'action',
    defaultValue: 'rgba(255, 255, 255, 0.62)',
    editable: false,
    derived: {
      from: '--mfw-palette-text-primary',
      transform: { kind: 'alpha', alpha: 0.62 },
    },
  },
  {
    varName: '--mfw-palette-action-selected',
    group: 'action',
    defaultValue: 'rgba(var(--mfw-palette-primary-mainChannel) / 0.08)',
    editable: false,
  },
  {
    varName: '--mfw-palette-ambient-accent',
    group: 'background',
    defaultValue: '#0e1d27',
    editable: false,
    derived: {
      from: '--mfw-palette-accent-main',
      transform: { kind: 'tone', L: 0.221, C: 0.028 },
    },
  },
  {
    varName: '--mfw-palette-ambient-brand',
    group: 'background',
    defaultValue: '#193320',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.294, C: 0.048 },
    },
  },
  {
    varName: '--mfw-palette-background-default',
    group: 'background',
    // Tones the primary hue into the surface ladder (D-8, D-12) — the whole
    // chrome now carries the brand hue, so a rebrand repaints all of it.
    defaultValue: '#070f09',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.156, C: 0.018 },
    },
  },
  {
    varName: '--mfw-palette-background-dim-10',
    group: 'background',
    defaultValue: 'rgba(7, 15, 9, 0.88)',
    editable: false,
    derived: {
      from: '--mfw-palette-background-default',
      transform: { kind: 'alpha', alpha: 0.88 },
    },
  },
  {
    varName: '--mfw-palette-background-dim-100',
    group: 'background',
    defaultValue: 'rgba(7, 15, 9, 0)',
    editable: false,
    derived: {
      from: '--mfw-palette-background-default',
      transform: { kind: 'alpha', alpha: 0 },
    },
  },
  {
    varName: '--mfw-palette-background-neutral',
    group: 'background',
    defaultValue: '#1f2821',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.265, C: 0.018 },
    },
  },
  {
    varName: '--mfw-palette-background-paper',
    group: 'background',
    defaultValue: '#121a13',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.206, C: 0.017 },
    },
  },
  {
    varName: '--mfw-palette-background-table-0',
    group: 'background',
    defaultValue: '#1f2821',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.265, C: 0.018 },
    },
  },
  {
    varName: '--mfw-palette-background-table-1',
    group: 'background',
    defaultValue: '#2a352c',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.315, C: 0.022 },
    },
  },
  {
    varName: '--mfw-palette-border-hairline',
    group: 'background',
    defaultValue: '#213426',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'tone', L: 0.305, C: 0.036 },
    },
  },
  /**
   * The ink of the surface as an `r g b` channel, for translucent tints that
   * must read on whatever the page is (`onBackgroundAlpha`: the player's chips,
   * card sheens and borders). It was the constant `255 255 255`, which is right
   * only on a dark ladder — on a light tile every one of those tints was white
   * on white. Derived from `text-primary`, so it is black on a light surface and
   * white on a dark one by the same rule, and from the same source, as the text
   * ladder: the chain is primary-main -> background-default -> text-primary ->
   * this. A dark island re-points it to `onDarkChannel` (`darkIsland.ts`), so
   * a tint drawn inside one keeps its white.
   */
  {
    varName: '--mfw-palette-common-onBackgroundChannel',
    group: 'common',
    defaultValue: '255 255 255',
    editable: false,
    derived: {
      from: '--mfw-palette-text-primary',
      transform: { kind: 'rgbChannel' },
    },
  },
  /**
   * Text/icons sitting on a field this app guarantees is dark — a photo or
   * video overlay, a scrim, the ambient hero panel. NOT the dark theme's text
   * colour: that is `text-primary`, which follows the surface and flips on a
   * tenant with a light ladder. Constant and NOT derived on purpose — the
   * field beneath it is dark by construction, so nothing about the palette
   * should move it. It exists so "this white is deliberate" is a token a
   * reader (and the colour-contract test) can see, instead of a judgement
   * call hidden behind every literal `#fff`.
   */
  {
    varName: '--mfw-palette-common-onDark',
    group: 'common',
    defaultValue: '#FFFFFF',
    editable: false,
  },
  {
    varName: '--mfw-palette-common-onDarkChannel',
    group: 'common',
    defaultValue: '255 255 255',
    editable: false,
    derived: {
      from: '--mfw-palette-common-onDark',
      transform: { kind: 'rgbChannel' },
    },
  },
  /**
   * Ink on the FILL, not on the surface — a contained button or a filled chip.
   * Was a hard-coded literal in `palette.ts` and wrong for three of the four
   * families: white on `success` read 2.28:1, on `error` 3.17:1. Derived here
   * so it follows a tenant that repaints its own status colour, exactly like
   * `primary-contrastText`. The default must equal the transform's output for
   * the default `main`, or a stored default diverges from a stored nothing.
   */
  {
    varName: '--mfw-palette-error-contrastText',
    group: 'error',
    defaultValue: '#000000',
    editable: false,
    derived: {
      from: '--mfw-palette-error-main',
      transform: { kind: 'contrast' },
    },
  },
  {
    varName: '--mfw-palette-error-dark',
    group: 'error',
    defaultValue: '#B71D18',
    editable: false,
  },
  {
    varName: '--mfw-palette-error-darker',
    group: 'error',
    defaultValue: '#7A0916',
    editable: false,
  },
  {
    varName: '--mfw-palette-error-light',
    group: 'error',
    defaultValue: '#FFAC82',
    editable: false,
  },
  {
    varName: '--mfw-palette-error-lighter',
    group: 'error',
    defaultValue: '#FFE9D5',
    editable: false,
  },
  {
    varName: '--mfw-palette-error-main',
    group: 'error',
    defaultValue: '#FF5630',
    editable: false,
  },
  {
    varName: '--mfw-palette-event-canceled',
    group: 'event',
    defaultValue: '#ef4444',
    editable: false,
  },
  {
    varName: '--mfw-palette-event-completed',
    group: 'event',
    defaultValue: '#22c55e',
    editable: false,
  },
  {
    varName: '--mfw-palette-event-missed',
    group: 'event',
    defaultValue: '#FFE9D5',
    editable: false,
  },
  {
    varName: '--mfw-palette-event-pending',
    group: 'event',
    defaultValue: '#f59e0b',
    editable: false,
  },
  {
    varName: '--mfw-palette-primary-contrastText',
    group: 'primary',
    // Verbatim '#000000' — MUST equal contrastTextColor(primary-main default)
    // so a seed/snapshot carrying the default primary recomputes to the SAME
    // value (no rebranded-vs-default divergence). Black wins because it SCORES
    // higher on brand green #28d771 — 11.05:1 against white's 1.90:1 — not
    // because the green clears a lightness threshold. D-11 retired the old
    // "L > 0.5 -> black" rule: black and white actually tie at L 0.179, so
    // that rule handed white to every colour in (0.179, 0.5], as low as
    // 2.14:1. Derived: recomputed via `contrastColorForRgb`
    // (themeResolution.ts) the moment an org/tenant overrides primary-main —
    // see mergeOrgTheme.
    defaultValue: '#000000',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'contrast' },
    },
  },
  {
    varName: '--mfw-palette-primary-dark',
    group: 'primary',
    defaultValue: '#00a953',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'shift', dL: -0.13, fC: 0.92 },
    },
  },
  {
    varName: '--mfw-palette-primary-darker',
    group: 'primary',
    defaultValue: '#007538',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'shift', dL: -0.28, fC: 0.75 },
    },
  },
  {
    varName: '--mfw-palette-primary-light',
    group: 'primary',
    defaultValue: '#6bea93',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'shift', dL: 0.07, fC: 0.85 },
    },
  },
  {
    varName: '--mfw-palette-primary-lighter',
    group: 'primary',
    defaultValue: '#acf7be',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'shift', dL: 0.14, fC: 0.55 },
    },
  },
  {
    varName: '--mfw-palette-primary-main',
    labelKey: 'themeVars.primaryMain',
    descriptionKey: 'themeVars.primaryMainDescription',
    group: 'primary',
    // Owner-picked 2026-07-21 (was '#29cc6c'): near-identical green whose
    // derived contrastText lands on BLACK at WCAG AA 11.05:1.
    defaultValue: '#28d771',
    editable: true,
  },
  {
    varName: '--mfw-palette-primary-mainChannel',
    group: 'primary',
    // '40 215 113' = rgbChannel of primary-main #28d771 (kept in lockstep —
    // a mainChannel/primary-main mismatch is the divergence class removed
    // 2026-07-20).
    defaultValue: '40 215 113',
    editable: false,
    derived: {
      from: '--mfw-palette-primary-main',
      transform: { kind: 'rgbChannel' },
    },
  },
  {
    varName: '--mfw-palette-secondary-dark',
    group: 'secondary',
    defaultValue: '#3f51b5',
    editable: false,
  },
  {
    varName: '--mfw-palette-secondary-darker',
    group: 'secondary',
    defaultValue: '#9c27b0',
    editable: false,
  },
  {
    varName: '--mfw-palette-secondary-light',
    group: 'secondary',
    defaultValue: '#ff7043',
    editable: false,
  },
  {
    varName: '--mfw-palette-secondary-lighter',
    group: 'secondary',
    defaultValue: '#ff9800',
    editable: false,
  },
  {
    varName: '--mfw-palette-secondary-main',
    group: 'secondary',
    defaultValue: '#06A0FF',
    editable: false,
  },
  /**
   * Ink on the FILL, not on the surface — a contained button or a filled chip.
   * Was a hard-coded literal in `palette.ts` and wrong for three of the four
   * families: white on `success` read 2.28:1, on `error` 3.17:1. Derived here
   * so it follows a tenant that repaints its own status colour, exactly like
   * `primary-contrastText`. The default must equal the transform's output for
   * the default `main`, or a stored default diverges from a stored nothing.
   */
  {
    varName: '--mfw-palette-success-contrastText',
    group: 'success',
    defaultValue: '#000000',
    editable: false,
    derived: {
      from: '--mfw-palette-success-main',
      transform: { kind: 'contrast' },
    },
  },
  {
    varName: '--mfw-palette-success-dark',
    group: 'success',
    defaultValue: '#118D57',
    editable: false,
  },
  {
    varName: '--mfw-palette-success-darker',
    group: 'success',
    defaultValue: '#065E49',
    editable: false,
  },
  {
    varName: '--mfw-palette-success-light',
    group: 'success',
    defaultValue: '#77ED8B',
    editable: false,
  },
  {
    varName: '--mfw-palette-success-lighter',
    group: 'success',
    defaultValue: '#D3FCD2',
    editable: false,
  },
  {
    varName: '--mfw-palette-success-main',
    group: 'success',
    defaultValue: '#22C55E',
    editable: false,
  },
  {
    varName: '--mfw-palette-text-disabled',
    group: 'text',
    defaultValue: 'rgba(255, 255, 255, 0.5)',
    editable: false,
    derived: {
      from: '--mfw-palette-text-primary',
      transform: { kind: 'alpha', alpha: 0.5 },
    },
  },
  {
    varName: '--mfw-palette-text-icon',
    group: 'text',
    defaultValue: 'rgba(255, 255, 255, 0.7)',
    editable: false,
    derived: {
      from: '--mfw-palette-text-primary',
      transform: { kind: 'alpha', alpha: 0.7 },
    },
  },
  /**
   * The anchor of the text ladder, and the one var that has to KNOW which end
   * of the surface ladder it is sitting on. `contrast` answers exactly that:
   * black or white, whichever the background can carry. Everything else in the
   * group is an alpha step of this, so one derivation flips all four.
   *
   * Not editable (rebrand v2): text colour is never chosen, it is always the
   * contrast colour of the background. `mergeOrgTheme` drops a stored value for
   * it — and for the four alpha steps below and `action-active` — from the org,
   * corp AND tenant layers before merging, so a legacy value left in a row has
   * no effect, and the write DTOs refuse it.
   */
  {
    varName: '--mfw-palette-text-primary',
    group: 'text',
    defaultValue: '#FFFFFF',
    editable: false,
    derived: {
      from: '--mfw-palette-background-default',
      transform: { kind: 'contrast' },
    },
  },
  /**
   * Was the owner-picked '#aab7c4' (2026-07-21), a blue-grey chosen because it
   * cleared 4.5:1 on all five DARK surfaces. An alpha step of `text-primary`
   * clears it on both ladders instead — 6.7:1 against the deepest dark surface,
   * 7.1:1 against a cream one — so the reason that colour was picked is now
   * satisfied by construction rather than by a measurement that only held for
   * one palette.
   */
  {
    varName: '--mfw-palette-text-secondary',
    group: 'text',
    defaultValue: 'rgba(255, 255, 255, 0.68)',
    editable: false,
    derived: {
      from: '--mfw-palette-text-primary',
      transform: { kind: 'alpha', alpha: 0.68 },
    },
  },
  // Was the hardcoded '#161c2428', i.e. the OLD background at ~15.7% alpha —
  // it silently stopped matching the background the moment anyone rebranded.
  {
    varName: '--mfw-palette-transparent',
    group: 'common',
    defaultValue: 'rgba(7, 15, 9, 0.157)',
    editable: false,
    derived: {
      from: '--mfw-palette-background-default',
      transform: { kind: 'alpha', alpha: 0.157 },
    },
  },
  /**
   * Ink on the FILL, not on the surface — a contained button or a filled chip.
   * Was a hard-coded literal in `palette.ts` and wrong for three of the four
   * families: white on `success` read 2.28:1, on `error` 3.17:1. Derived here
   * so it follows a tenant that repaints its own status colour, exactly like
   * `primary-contrastText`. The default must equal the transform's output for
   * the default `main`, or a stored default diverges from a stored nothing.
   */
  {
    varName: '--mfw-palette-warning-contrastText',
    group: 'warning',
    defaultValue: '#000000',
    editable: false,
    derived: {
      from: '--mfw-palette-warning-main',
      transform: { kind: 'contrast' },
    },
  },
  {
    varName: '--mfw-palette-warning-dark',
    group: 'warning',
    defaultValue: '#B76E00',
    editable: false,
  },
  {
    varName: '--mfw-palette-warning-darker',
    group: 'warning',
    defaultValue: '#7A4100',
    editable: false,
  },
  {
    varName: '--mfw-palette-warning-light',
    group: 'warning',
    defaultValue: '#FFD666',
    editable: false,
  },
  {
    varName: '--mfw-palette-warning-lighter',
    group: 'warning',
    defaultValue: '#FFF5CC',
    editable: false,
  },
  {
    varName: '--mfw-palette-warning-main',
    group: 'warning',
    defaultValue: '#FFAB00',
    editable: false,
  },
];

/**
 * The five surface vars.
 *
 * They are `derived` + `editable: false` in the catalog — the surface ladder
 * follows the brand hue, which is what makes the whole chrome read as one
 * material. But the tenant page could set them before this change, and dropping
 * that silently is the failure mode the SEMANTIC_THEME_VAR_NAMES comment (in
 * `dtos/rebrand.ts`) warns about, so the tenant write path keeps them as a
 * separate layer.
 *
 * It cannot reuse the semantic filter (`!editable && !derived`): these vars ARE
 * derived. Hence an explicit list.
 *
 * Precedence, from `mergeOrgTheme`: a STORED surface is never recomputed from
 * its source. A tenant's pinned ladder survives the tenant's own `primary-main`
 * edit AND an org rebrand's — an org's brand colour is no surface choice, so
 * repainting from it would only erase the tenant's choice. Without a pin the
 * ladder is toned from whichever layer's `primary-main` wins. An org chooses a
 * surface of its own only through an explicit background tile
 * (`surfaceTiles.ts`), which `mergeOrgTheme` places on the org layer above the
 * pin.
 *
 * Lives in the catalog rather than with the DTOs because `mergeOrgTheme` reads
 * it, and the DTO module already imports the theme module.
 */
export const SURFACE_THEME_VAR_NAMES: readonly string[] = [
  '--mfw-palette-background-default',
  '--mfw-palette-background-neutral',
  '--mfw-palette-background-paper',
  '--mfw-palette-background-table-0',
  '--mfw-palette-background-table-1',
];

/**
 * The five text and action vars (rebrand v2): the text ladder and the control
 * ink, every one derived from the surface and none of them editable.
 *
 * Text colour is never chosen — it is always the contrast colour of the
 * background, so readability is guaranteed by construction rather than by
 * whoever picks. The vars stay in the catalog (the whole app reads them as CSS
 * variables, and `THEME_CONTRAST_CHECKS` grade them), but no layer may SET one:
 * the write DTOs refuse them, and `mergeOrgTheme` discards a stored value from
 * every layer before merging, so a legacy manual override left in a row has no
 * effect even on a deployment where the strip migration has not run yet.
 *
 * An explicit list rather than a catalog query, like `SURFACE_THEME_VAR_NAMES`:
 * what is stripped from a stored theme must not move because a var happened to
 * gain a `derived` entry. `theme-vars.spec.ts` pins that each one is derived
 * and not editable.
 */
export const AUTOMATIC_TEXT_THEME_VAR_NAMES: readonly string[] = [
  '--mfw-palette-action-active',
  '--mfw-palette-text-disabled',
  '--mfw-palette-text-icon',
  '--mfw-palette-text-primary',
  '--mfw-palette-text-secondary',
];

/**
 * The non-text derived vars the merge STRIPS from a stored tenant layer — not
 * every derived var: a legacy row may still hold other raw derived values, and
 * those pass through as stored (`mergeOrgTheme`). A var is listed here when a
 * stored value is known to outrank its derivation in a way that breaks the
 * page. Kept apart from `AUTOMATIC_TEXT_THEME_VAR_NAMES` on purpose, because
 * that list means "the five text and action vars" to its other readers (the
 * strip migration's frozen copy, the editor's contrast labels) and must not
 * change meaning.
 *
 * `common-onBackgroundChannel` is the surface-tint channel and follows the ink of
 * the surface (`text-primary`), so it is derived. It used to be a plain constant,
 * and the old tenant page stored its saving admin's whole resolved palette: a
 * tenant row can still hold `255 255 255` for it. `mergeOrgTheme` discards a
 * stored value from the tenant layer, exactly as it does for the text vars —
 * left in, that value would sit at the tenant layer and outrank the derivation
 * for a tenant that pinned a light ladder of its own, turning every
 * `onBackgroundAlpha` tint white on white.
 *
 * An explicit list rather than a catalog query, like its siblings: what is
 * stripped from a stored theme must not move because a var happened to gain a
 * `derived` entry. `theme-vars.spec.ts` pins that each one is derived and not
 * editable.
 */
export const DERIVED_ONLY_THEME_VAR_NAMES: readonly string[] = [
  '--mfw-palette-common-onBackgroundChannel',
];
